/**
 * Pure aggregation logic for the GroupDetail «Needs attention» panel.
 *
 * `buildGroupAlerts` is a side-effect-free projection over the six already
 * computed source counts (debtors, below-threshold attendance, students
 * without marks, overdue contributions, failed Zoom recordings, and
 * missing/lost PandaDoc documents). It decides which alerts are shown, what
 * count each carries, and where each one deep-links to.
 *
 * This module intentionally avoids importing React, Apollo, generated GraphQL
 * types, or the GroupDetail component files: the `TabId`/`RosterFilter` unions
 * are declared here as the canonical source so callers/components import them
 * from this util rather than the other way round.
 */

/**
 * Вкладки карточки группы — ЕДИНСТВЕННЫЙ список на приложение, в порядке
 * слева направо.
 *
 * 🔴 До ТЗ 0041 списков было три: союз здесь, массив в
 * `components/pages/GroupDetail/tabIds.ts` и ещё один массив-белый-список в
 * `groupDeepLink.ts`. Новая вкладка «Навыки» попала в первые два и не попала
 * в третий — и `?tab=skills` молча открывал «Обзор». Ни tsc, ни сборка, ни
 * тесты этого не увидели: пропущенный элемент массива `readonly TabId[]` —
 * законный массив, ошибкой становится только ЛИШНИЙ. Поймано браузером.
 *
 * Теперь союз выводится из массива, а оба потребителя берут этот массив, —
 * забыть строку больше негде.
 */
export const GROUP_TAB_IDS = [
  'overview',
  'students',
  'schedule',
  // ТЗ 0123 — «Аналитика занятий» методиста: сразу после «Расписания».
  'lessonAnalytics',
  'zoom',
  'pandadoc',
  'materials',
  'skills',
  'surveys',
  'homework',
  'communication',
  'mailings',
  'tasks',
  'finances',
  'diplomas',
] as const;

export type TabId = typeof GROUP_TAB_IDS[number];

/**
 * The roster filter chips on the Students tab. Mirrors the union in
 * `components/pages/GroupDetail/parts/StudentRosterFilters.tsx`.
 */
export type RosterFilter =
  | 'all'
  | 'active'
  | 'terminated'
  | 'debtors'
  | 'blocked';

/** The six fixed alert categories surfaced by the panel. */
export type GroupAlertId =
  | 'debtors'
  | 'attendanceBelow'
  | 'noMarks'
  | 'overdue'
  | 'zoomFailed'
  | 'pandadocMissing';

/** Where selecting an alert navigates to (and the filter it applies, if any). */
export interface IGroupAlertTarget {
  tab: TabId;
  filter?: RosterFilter;
}

/** A single displayable alert: its category, affected-item count, and target. */
export interface IGroupAlert {
  id: GroupAlertId;
  count: number;
  target: IGroupAlertTarget;
}

/**
 * The already-computed state of one alert category as supplied by its source
 * hook: the affected-item count plus whether that source is still loading.
 *
 * `enabled: false` — роль этот источник не читает: его запрос не уходит, и в
 * итог он не входит ни счётчиком, ни загрузкой, ни «проблем нет».
 * `failed: true` — запрос источника упал (отказ или сбой): ноль у такого
 * источника — не «проблем нет», а «не знаем».
 */
export interface IGroupAlertSource {
  count: number;
  loading: boolean;
  /** По умолчанию `true`. */
  enabled?: boolean;
  /** По умолчанию `false`. */
  failed?: boolean;
}

/** Какие источники роль читает — по одному флагу на категорию. */
export type GroupAlertAccess = Readonly<Record<GroupAlertId, boolean>>;

/**
 * Decoupled input for `buildGroupAlerts` — one `{ count, loading }` entry per
 * category. Callers (the `AlertsPanel`) map their six source hooks onto these
 * fields; the util never touches the hooks directly.
 */
export interface IBuildGroupAlertsInput {
  debtors: IGroupAlertSource;
  attendanceBelow: IGroupAlertSource;
  noMarks: IGroupAlertSource;
  overdue: IGroupAlertSource;
  zoomFailed: IGroupAlertSource;
  pandadocMissing: IGroupAlertSource;
}

/** The derived alerts state consumed by the panel. */
export interface IGroupAlertsState {
  /** Alerts already filtered to `count > 0`, in canonical category order. */
  alerts: IGroupAlert[];
  /** `true` iff every evaluated category has finished loading. */
  allLoaded: boolean;
  /** `true` iff at least one category is still loading. */
  anyLoading: boolean;
  /** `true` iff at least one enabled category failed to load. */
  anyFailed: boolean;
  /** How many categories the role may read at all (`enabled`). */
  enabledCount: number;
}

/**
 * Fixed category → navigation-target mapping. The targets are constant: an
 * alert always deep-links to the same tab (and filter) regardless of counts.
 */
export const GROUP_ALERT_TARGETS: Readonly<
  Record<GroupAlertId, IGroupAlertTarget>
> = {
  debtors: { tab: 'students', filter: 'debtors' },
  attendanceBelow: { tab: 'schedule' },
  noMarks: { tab: 'schedule' },
  overdue: { tab: 'finances' },
  zoomFailed: { tab: 'zoom' },
  pandadocMissing: { tab: 'pandadoc' },
};

/** Canonical display order for the alert categories. */
export const GROUP_ALERT_ORDER: readonly GroupAlertId[] = [
  'debtors',
  'attendanceBelow',
  'noMarks',
  'overdue',
  'zoomFailed',
  'pandadocMissing',
];

/**
 * Build the «Needs attention» alerts state from the six source counts.
 *
 * - `alerts` contains one entry per category whose `count > 0`, in canonical
 *   order, each carrying its true count and its fixed navigation target.
 * - `allLoaded` is `true` iff every category has finished loading.
 * - `anyLoading` is `true` iff at least one category is still loading.
 */
export const buildGroupAlerts = (
  input: IBuildGroupAlertsInput,
): IGroupAlertsState => {
  const alerts: IGroupAlert[] = [];
  let allLoaded = true;
  let anyLoading = false;
  let anyFailed = false;
  let enabledCount = 0;

  GROUP_ALERT_ORDER.forEach((id) => {
    const { count, loading, enabled = true, failed = false } = input[id];
    if (!enabled) return;
    enabledCount += 1;

    if (loading) {
      anyLoading = true;
      allLoaded = false;
    }

    if (failed) {
      anyFailed = true;
    }

    if (count > 0) {
      alerts.push({ id, count, target: GROUP_ALERT_TARGETS[id] });
    }
  });

  return { alerts, allLoaded, anyLoading, anyFailed, enabledCount };
};

/**
 * Whether the localized «no issues» state may be shown: only once every
 * readable category has finished loading WITHOUT an error and none produced a
 * positive count. A failed source is «не знаем», not «ноль»; with no readable
 * source at all there is nothing to vouch for. Derivable purely from the
 * returned state, exposed here for callers' convenience.
 */
export const canShowNoIssues = (state: IGroupAlertsState): boolean =>
  state.allLoaded &&
  !state.anyFailed &&
  state.enabledCount > 0 &&
  state.alerts.length === 0;
